import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Zap, ExternalLink, Sparkles, Phone, MapPin, Clock, Calendar, Star, User } from 'lucide-react';
import { LOGO_URL, EMS_SITE_URL, BOOKING_URL } from '../data/constants';
import { useLanguage } from '../context/LanguageContext';
import { useClientAuth } from '../context/ClientAuthContext';
import { AppBadgeBanner } from './shared';
import { ThemeToggle } from './ThemeToggle';

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const { isAuthed } = useClientAuth();
  const isFr = language === 'fr';
  const clientLink = isAuthed ? '/mon-espace' : '/espace-client';
  const clientLabel = isFr ? (isAuthed ? 'Mon espace' : 'Espace client') : (isAuthed ? 'My area' : 'Client area');
  const fixedBarRef = useRef(null);

  // Le bandeau "essai gratuit" + le header sont en position fixed (empilés) et leur hauteur
  // totale varie (bandeau visible/masqué, notch mobile via safe-area, breakpoint) — on mesure
  // la hauteur RÉELLE rendue et on l'expose en variable CSS pour que les pages (ex: le badge
  // "Studio privé..." de la page d'accueil) puissent réserver exactement l'espace nécessaire
  // sans jamais être masquées, sur tous les supports, même quand le bandeau est fermé.
  useLayoutEffect(() => {
    const el = fixedBarRef.current;
    if (!el) return;
    const update = () => document.documentElement.style.setProperty('--app-header-h', `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  const navGroups = [
    { name: t.nav.home, path: "/" },
    {
      name: "Coaching",
      items: [
        { name: t.nav.about, path: "/about" },
        { name: t.nav.services, path: "/services" },
        { name: "Tarifs", path: "/tarifs" },
        { name: isFr ? "Coaching Hybride" : "Hybrid Coaching", path: "/parcours/coaching-hybride" },
        { name: isFr ? "Coaching 100% en ligne" : "100% Online Coaching", path: "/parcours/coaching-online" },
        { name: isFr ? "Horaires" : "Hours", path: "/horaires" },
        { name: "FAQ", path: "/faq" },
      ],
    },
    { name: isFr ? "L'App Body-Up" : "The Body-Up App", path: "/application" },
    { name: "Blog", path: "/blog" },
    { name: t.nav.contact, path: "/contact" },
  ];

  const inGroup = (items) => items?.some((i) => location.pathname === i.path || location.pathname.startsWith(i.path + "/"));

  return (
    <>
      {/* Skip to main content - Accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded">
        Aller au contenu principal
      </a>
      <div className="fixed top-0 left-0 right-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }} ref={fixedBarRef}>
        <AppBadgeBanner />
        <header
          className="bg-black border-b border-white/10"
          data-testid="header"
          role="banner"
        >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-24">
          {/* Logo - 20% bigger to match trainiq-ems.ch */}
          <Link to="/" className="flex-shrink-0 transition-transform hover:scale-105" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Body-Up Personal Training - Coach Sportif Privé à Nyon" 
              className="h-10 sm:h-14 w-auto" 
              width="160" 
              height="56"
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navGroups.map((group) =>
              group.items ? (
                <div key={group.name} className="relative group" data-testid={`nav-group-${group.name.toLowerCase()}`}>
                  <button
                    className={`flex items-center gap-1 px-3 py-2 text-[15px] font-medium transition-colors hover:text-primary ${inGroup(group.items) ? "text-primary" : "text-white"}`}
                  >
                    {group.name}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="bg-zinc-900 border border-white/10 rounded-2xl p-2 min-w-[230px] shadow-2xl shadow-black/50">
                      {group.items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors hover:bg-white/5 hover:text-primary ${location.pathname === item.path ? "text-primary bg-white/5" : "text-zinc-300"}`}
                          data-testid={`nav-item-${item.path.replace(/\//g, "")}`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={group.path}
                  to={group.path}
                  className={`relative px-3 py-2 text-[15px] font-medium transition-all hover:text-primary group ${location.pathname === group.path ? "text-primary" : "text-white"}`}
                  data-testid={`nav-${group.path.replace(/\//g, "") || "home"}`}
                >
                  {group.name}
                  <span className="absolute bottom-0 left-3 w-0 h-0.5 bg-primary transition-all group-hover:w-[calc(100%-1.5rem)]" />
                </Link>
              )
            )}
          </nav>

          {/* EMS Badge + Language + CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Language Switcher - Segmented Control (matching body-up-ems.ch) */}
            <div className="flex items-center bg-zinc-800/80 rounded-full border border-zinc-700/50 p-0.5" data-testid="lang-switch">
              <button
                onClick={() => language !== 'fr' && toggleLanguage()}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 ${language === 'fr' ? 'bg-primary text-white shadow-md shadow-primary/30' : 'text-zinc-400 hover:text-white'}`}
                data-testid="lang-switch-fr"
              >
                FR
              </button>
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 ${language === 'en' ? 'bg-primary text-white shadow-md shadow-primary/30' : 'text-zinc-400 hover:text-white'}`}
                data-testid="lang-switch-en"
              >
                EN
              </button>
            </div>
            
            {/* Bascule thème clair/sombre */}
            <ThemeToggle compact />

            {/* Espace client */}
            <Link
              to={clientLink}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white border border-white/15 hover:border-primary hover:text-primary transition-all"
              data-testid="client-area-link"
            >
              <User className="w-3.5 h-3.5" />
              {clientLabel}
            </Link>

            {/* EMS Badge - Compact */}
            <a
              href={EMS_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/15 border border-primary/40 rounded-full text-xs font-semibold text-primary hover:bg-primary/25 transition-all"
              data-testid="ems-badge"
            >
              <Zap className="w-3 h-3" />
              {language === 'fr' ? 'EMS Groupe' : 'Group EMS'}
              <ExternalLink className="w-2.5 h-2.5 opacity-60" />
            </a>


          </div>

          {/* Mobile: Language Switcher + Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            {/* Language Switcher - Mobile (matching body-up-ems.ch) */}
            <div className="flex items-center bg-zinc-800/80 rounded-full border border-zinc-700/50 p-0.5" data-testid="lang-switch-mobile">
              <button
                onClick={() => language !== 'fr' && toggleLanguage()}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${language === 'fr' ? 'bg-primary text-white' : 'text-zinc-400'}`}
              >
                FR
              </button>
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${language === 'en' ? 'bg-primary text-white' : 'text-zinc-400'}`}
              >
                EN
              </button>
            </div>
            
            {/* Bascule thème clair/sombre - Mobile */}
            <ThemeToggle compact />

            {/* Menu Button */}
            <button
              className="text-white p-2 hover:text-primary transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden bg-black border-t border-white/10 transition-all duration-300 ${isMobileMenuOpen ? "max-h-screen opacity-100 overflow-y-auto" : "max-h-0 opacity-0 overflow-hidden"}`} data-testid="mobile-menu">
        <div className="px-4 py-5 space-y-1">
          {navGroups.map((group) =>
            group.items ? (
              <div key={group.name} className="border-b border-white/5 pb-1">
                <button
                  onClick={() => setOpenGroup(openGroup === group.name ? null : group.name)}
                  className={`w-full flex items-center justify-between py-3 font-semibold transition-colors ${inGroup(group.items) ? "text-primary" : "text-white"}`}
                  data-testid={`mobile-group-${group.name.toLowerCase()}`}
                >
                  {group.name}
                  <ChevronDown className={`w-4 h-4 transition-transform ${openGroup === group.name ? "rotate-180" : ""}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openGroup === group.name ? "max-h-96" : "max-h-0"}`}>
                  <div className="pl-3 pb-2 space-y-1">
                    {group.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block py-2 text-sm transition-colors hover:text-primary ${location.pathname === item.path ? "text-primary" : "text-zinc-300"}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={group.path}
                to={group.path}
                className={`block py-3 font-semibold border-b border-white/5 transition-colors hover:text-primary ${location.pathname === group.path ? "text-primary" : "text-white"}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {group.name}
              </Link>
            )
          )}
          <Link
            to={clientLink}
            className="flex items-center gap-2 text-white hover:text-primary py-3 transition-colors"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="client-area-link-mobile"
          >
            <User className="w-4 h-4" />
            {clientLabel}
          </Link>
          <a
            href={EMS_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-primary py-2"
          >
            <Sparkles className="w-4 h-4" />
            {t.emsBadge.discover}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </header>
    </div>
    </>
  );
};

// Footer Component - WAHOU Design
